import { Search, X } from 'lucide-react';
import { BudgetFilterOptions, BudgetFetchParams, BUDGET_STATUSES, FUND_TYPES, BILL_CATEGORIES } from '../../types/budget';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import FilterActions from '../FilterActions';

interface BudgetFiltersProps {
  searchQuery: string;
  filters: Partial<BudgetFetchParams>;
  filterOptions: BudgetFilterOptions | null;
  onSearchChange: (query: string) => void;
  onFilterChange: (filters: Partial<BudgetFetchParams>) => void;
  onClearFilters: () => void;
  variant?: 'full' | 'compact';
}

const BudgetFilters: React.FC<BudgetFiltersProps> = ({
  searchQuery,
  filters,
  filterOptions,
  onSearchChange,
  onFilterChange,
  onClearFilters,
  variant = 'full',
}) => {
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const hasActiveFilters =
    searchQuery !== '' ||
    !!filters.dept_code ||
    !!filters.fiscal_year ||
    !!filters.status ||
    !!filters.fund_type ||
    !!filters.bill_number ||
    !!filters.bill_category ||
    filters.has_fte_changes === 'true' ||
    filters.min_amount != null ||
    filters.max_amount != null;

  if (isMobile || variant === 'compact') {
    return (
      <div className="col-span-full">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
          <input
            type="text"
            placeholder="Search budget items..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary placeholder-text-tertiary dark:placeholder-text-dark-tertiary focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-2 space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
        <input
          type="text"
          placeholder="Search budget items by title or item number..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary placeholder-text-tertiary dark:placeholder-text-dark-tertiary focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filter row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Department */}
        <select
          value={filters.dept_code || ''}
          onChange={(e) => onFilterChange({ dept_code: e.target.value || undefined })}
          className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm"
        >
          <option value="">All Departments</option>
          {filterOptions?.departments.map((d) => (
            <option key={d.dept_code} value={d.dept_code}>
              {d.dept_name}
            </option>
          ))}
        </select>

        {/* Fiscal Year */}
        <select
          value={filters.fiscal_year || ''}
          onChange={(e) => onFilterChange({ fiscal_year: e.target.value || undefined })}
          className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm"
        >
          <option value="">All Fiscal Years</option>
          {filterOptions?.fiscalYears.map((fy) => (
            <option key={fy} value={fy}>
              FY {fy}
            </option>
          ))}
        </select>

        {/* Status */}
        <select
          value={filters.status || ''}
          onChange={(e) => onFilterChange({ status: e.target.value || undefined })}
          className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm"
        >
          <option value="">All Statuses</option>
          {BUDGET_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        {/* Fund Type */}
        <select
          value={filters.fund_type || ''}
          onChange={(e) => onFilterChange({ fund_type: e.target.value || undefined })}
          className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm"
        >
          <option value="">All Fund Types</option>
          {FUND_TYPES.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      {/* Second filter row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Bill Number */}
        <input
          type="text"
          placeholder="Related bill number..."
          value={filters.bill_number || ''}
          onChange={(e) => onFilterChange({ bill_number: e.target.value || undefined })}
          className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary placeholder-text-tertiary dark:placeholder-text-dark-tertiary text-sm"
        />

        {/* Bill Category */}
        <select
          value={filters.bill_category || ''}
          onChange={(e) => onFilterChange({ bill_category: e.target.value || undefined })}
          className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary text-sm"
        >
          <option value="">All Bill Categories</option>
          {BILL_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        {/* FTE Changes Toggle */}
        <label className="flex items-center gap-2 px-3 py-2 text-sm text-text-secondary dark:text-text-dark-secondary">
          <input
            type="checkbox"
            checked={filters.has_fte_changes === 'true'}
            onChange={(e) => onFilterChange({ has_fte_changes: e.target.checked ? 'true' : undefined })}
            className="w-4 h-4 text-primary-600 rounded border-border-light dark:border-border-dark focus:ring-primary-500"
          />
          FTE Changes Only
        </label>

        <FilterActions onClear={onClearFilters} hasFilters={hasActiveFilters} />
      </div>
    </div>
  );
};

export default BudgetFilters;
